import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import crmService from "../../crmConfig.json";
import { crmAxios } from "../../app/crmApi";
import { errorToast, successToast } from "../../helpers/toaster";

const initialState = {
    hotel: { list: [], pagination: { page: 1, limit: 20, total: 0, totalPages: 0 }, selected: null },
    flight: { list: [], pagination: { page: 1, limit: 20, total: 0, totalPages: 0 }, selected: null },
    visa: { list: [], pagination: { page: 1, limit: 20, total: 0, totalPages: 0 }, selected: null },
    users: { list: [], pagination: { page: 1, limit: 20, total: 0, totalPages: 0 }, selected: null },
    loading: false,
    saving: false,
};

const bookingConfig = {
    hotel: { endpoint: crmService.hotelBookings, dataKey: "bookings" },
    flight: { endpoint: crmService.flightBookings, dataKey: "bookings" },
    visa: { endpoint: crmService.visaBookings, dataKey: "bookings" },
};

const makeBookingThunks = (moduleKey) => {
    const { endpoint, dataKey } = bookingConfig[moduleKey];

    const fetchList = createAsyncThunk(
        `crmBookings/${moduleKey}/fetchList`,
        async (params, { rejectWithValue }) => {
            try {
                const res = await crmAxios.get(endpoint, { params });
                return res.data;
            } catch (err) {
                return rejectWithValue(err.response?.data);
            }
        }
    );

    const fetchById = createAsyncThunk(
        `crmBookings/${moduleKey}/fetchById`,
        async (id, { rejectWithValue }) => {
            try {
                const res = await crmAxios.get(`${endpoint}/${id}`);
                return res.data;
            } catch (err) {
                return rejectWithValue(err.response?.data);
            }
        }
    );

    const updateStatus = createAsyncThunk(
        `crmBookings/${moduleKey}/updateStatus`,
        async ({ id, status }, { rejectWithValue }) => {
            try {
                const res = await crmAxios.patch(`${endpoint}/${id}/status`, {
                    status,
                });
                return res.data;
            } catch (err) {
                return rejectWithValue(err.response?.data);
            }
        }
    );

    return { fetchList, fetchById, updateStatus, dataKey };
};

export const hotelBookingThunks = makeBookingThunks("hotel");
export const flightBookingThunks = makeBookingThunks("flight");
export const visaBookingThunks = makeBookingThunks("visa");

export const fetchHotelBookingsAsync = hotelBookingThunks.fetchList;
export const fetchHotelBookingByIdAsync = hotelBookingThunks.fetchById;
export const updateHotelBookingStatusAsync = hotelBookingThunks.updateStatus;

export const fetchFlightBookingsAsync = flightBookingThunks.fetchList;
export const fetchFlightBookingByIdAsync = flightBookingThunks.fetchById;
export const updateFlightBookingStatusAsync = flightBookingThunks.updateStatus;

export const fetchVisaBookingsAsync = visaBookingThunks.fetchList;
export const fetchVisaBookingByIdAsync = visaBookingThunks.fetchById;
export const updateVisaBookingStatusAsync = visaBookingThunks.updateStatus;

// --- Site users ---
export const fetchSiteUsersAsync = createAsyncThunk(
    "crmBookings/users/fetchList",
    async (params, { rejectWithValue }) => {
        try {
            const res = await crmAxios.get(crmService.siteUsers, { params });
            return res.data;
        } catch (err) {
            return rejectWithValue(err.response?.data);
        }
    }
);

export const fetchSiteUserByIdAsync = createAsyncThunk(
    "crmBookings/users/fetchById",
    async (id, { rejectWithValue }) => {
        try {
            const res = await crmAxios.get(`${crmService.siteUsers}/${id}`);
            return res.data;
        } catch (err) {
            return rejectWithValue(err.response?.data);
        }
    }
);

export const setSiteUserActivationAsync = createAsyncThunk(
    "crmBookings/users/setActivation",
    async ({ id, activate }, { rejectWithValue }) => {
        try {
            const res = await crmAxios.patch(
                `${crmService.siteUsers}/${id}/${activate ? "activate" : "deactivate"}`
            );
            return res.data;
        } catch (err) {
            return rejectWithValue(err.response?.data);
        }
    }
);

const thunksByModule = {
    hotel: hotelBookingThunks,
    flight: flightBookingThunks,
    visa: visaBookingThunks,
};

const crmBookingsSlice = createSlice({
    name: "crmBookings",
    initialState,
    reducers: {
        clearSelectedBooking: (state, { payload: moduleKey }) => {
            state[moduleKey].selected = null;
        },
        clearSelectedUser: (state) => {
            state.users.selected = null;
        },
    },
    extraReducers: (builder) => {
        ["hotel", "flight", "visa"].forEach((moduleKey) => {
            const { fetchList, fetchById, updateStatus, dataKey } = thunksByModule[moduleKey];
            builder
                .addCase(fetchList.pending, (state) => {
                    state.loading = true;
                })
                .addCase(fetchList.fulfilled, (state, { payload }) => {
                    state.loading = false;
                    if (payload?.success) {
                        state[moduleKey].list = payload.data[dataKey];
                        state[moduleKey].pagination = payload.data.pagination;
                    }
                })
                .addCase(fetchList.rejected, (state, { payload }) => {
                    state.loading = false;
                    errorToast(payload?.message || "Failed to load bookings");
                })
                .addCase(fetchById.fulfilled, (state, { payload }) => {
                    if (payload?.success) state[moduleKey].selected = payload.data;
                })
                .addCase(updateStatus.pending, (state) => {
                    state.saving = true;
                })
                .addCase(updateStatus.fulfilled, (state, { payload }) => {
                    state.saving = false;
                    if (payload?.success) {
                        successToast(payload.message);
                        state[moduleKey].selected = payload.data;
                    } else {
                        errorToast(payload?.message || "Update failed");
                    }
                })
                .addCase(updateStatus.rejected, (state, { payload }) => {
                    state.saving = false;
                    errorToast(payload?.message || "Update failed");
                });
        });

        builder
            .addCase(fetchSiteUsersAsync.pending, (state) => {
                state.loading = true;
            })
            .addCase(fetchSiteUsersAsync.fulfilled, (state, { payload }) => {
                state.loading = false;
                if (payload?.success) {
                    state.users.list = payload.data.users;
                    state.users.pagination = payload.data.pagination;
                }
            })
            .addCase(fetchSiteUsersAsync.rejected, (state, { payload }) => {
                state.loading = false;
                errorToast(payload?.message || "Failed to load users");
            })
            .addCase(fetchSiteUserByIdAsync.fulfilled, (state, { payload }) => {
                if (payload?.success) state.users.selected = payload.data;
            })
            .addCase(setSiteUserActivationAsync.pending, (state) => {
                state.saving = true;
            })
            .addCase(setSiteUserActivationAsync.fulfilled, (state, { payload }) => {
                state.saving = false;
                if (payload?.success) {
                    successToast(payload.message);
                    state.users.selected = payload.data;
                    state.users.list = state.users.list.map((u) =>
                        u.id === payload.data.id ? payload.data : u
                    );
                } else {
                    errorToast(payload?.message || "Update failed");
                }
            })
            .addCase(setSiteUserActivationAsync.rejected, (state, { payload }) => {
                state.saving = false;
                errorToast(payload?.message || "Update failed");
            });
    },
});

export const { clearSelectedBooking, clearSelectedUser } = crmBookingsSlice.actions;

export const selectHotelBookings = (state) => state.crmBookings.hotel;
export const selectFlightBookings = (state) => state.crmBookings.flight;
export const selectVisaBookings = (state) => state.crmBookings.visa;
export const selectSiteUsers = (state) => state.crmBookings.users;
export const selectBookingsLoading = (state) => state.crmBookings.loading;
export const selectBookingsSaving = (state) => state.crmBookings.saving;

export default crmBookingsSlice.reducer;
